import { Color3, Mesh, MeshBuilder, Vector3, type Scene, type ShaderMaterial, type StandardMaterial } from '@babylonjs/core';
import type { ItemRarity } from '@sorrem/shared';
import { EssenceThread, type EssenceThreadTuning } from './essence-thread';
import { createSpriteMaterial, type ParticleSprite } from './particle-sprites';
import { createPrimedGlyphMaterial, PRIMED_GLYPH_MAX_SOCKETS, PRIMED_GLYPH_STYLE_INDEX } from './primed-glyph-system';
import { smoothstep } from './easing';

/** The beams a dropped item can stand under, one per rarity from Touched up (#259); Hollow items have none. */
export type LootBeamTier = 'touched' | 'attuned' | 'resonant' | 'unforgotten' | 'empyreal';

export const LOOT_BEAM_TIERS: readonly LootBeamTier[] = ['touched', 'attuned', 'resonant', 'unforgotten', 'empyreal'];

const TIER_BY_RARITY: Readonly<Record<ItemRarity, LootBeamTier | null>> = {
    common: null,
    uncommon: 'touched',
    rare: 'attuned',
    epic: 'resonant',
    legendary: 'unforgotten',
    mythic: 'empyreal',
};

/** The beam an item of `rarity` stands under, or null for none (Hollow). */
export function resolveLootBeamTier(rarity: ItemRarity): LootBeamTier | null {
    return TIER_BY_RARITY[rarity];
}

/** How a tier's beam reads: each tier is the one below it plus one thing. */
export interface LootBeamLook {
    /** The core's brightness: Touched's is faint. */
    readonly brightness: number;
    /** The core's width, as a share of an Attuned core's. */
    readonly widthShare: number;
    /** White streams spinning wide around the core, the other way to it. */
    readonly outerStreams: number;
    /** A ring spreads over the floor now and then. */
    readonly pulse: boolean;
    /** The outer streams pinch in and tie off around the core at its top, where a light gathers. */
    readonly knot: boolean;
    /** The Veil's glyph stands over the knot. */
    readonly glyph: boolean;
}

export const LOOT_BEAM_LOOKS: Readonly<Record<LootBeamTier, LootBeamLook>> = {
    touched: { brightness: 0.3, widthShare: 0.7, outerStreams: 0, pulse: false, knot: false, glyph: false },
    attuned: { brightness: 0.6, widthShare: 1, outerStreams: 0, pulse: false, knot: false, glyph: false },
    resonant: { brightness: 0.7, widthShare: 1, outerStreams: 1, pulse: false, knot: false, glyph: false },
    unforgotten: { brightness: 0.8, widthShare: 1, outerStreams: 2, pulse: true, knot: true, glyph: false },
    empyreal: { brightness: 0.9, widthShare: 1, outerStreams: 2, pulse: true, knot: true, glyph: true },
};

/** What a dropped item's beam is drawn from. */
export interface LootBeamSpec {
    readonly tier: LootBeamTier;
    /**
     * The core's colors: the essence types the item's affixes carry, one straight
     * thread or several twirling about each other; Resonance alone when it
     * carries none. Never the rarity's color, which the essence colors
     * would be mistaken for: the beam's shape and the floor mark say the
     * tier (`LootFloorMark`).
     */
    readonly coreColorHexes: readonly string[];
}

/** The Veil's own light: the outer streams, the floor pulse, the knot and the glyph. */
const VEIL_LIGHT = Color3.FromHexString('#eef4ff');

/** How tall a beam stands, from the floor. */
export const LOOT_BEAM_HEIGHT_TILES = 2.4;

const CORE_WIDTH_TILES = 0.24;
const CORE_STRENGTH = 0.8;
const CORE_SEGMENTS = 24;
/** Several essence types: each is a thinner thread, twirling tight about the beam's axis. */
const TWIN_CORE_WIDTH_TILES = 0.16;
const TWIN_CORE_RADIUS_TILES = 0.035;
const TWIN_CORE_TURNS_PER_TILE = 1.2;
const TWIN_CORE_SPIN = 1.4;
const TWIN_CORE_SEGMENTS = 48;
/** The outer streams: thin, near white, in wide, slow spins the other way, so they never read as part of the core. */
const OUTER_WIDTH_TILES = 0.1;
const OUTER_STRENGTH = 0.5;
const OUTER_RADIUS_TILES = 0.17;
const OUTER_TURNS_PER_TILE = 0.5;
const OUTER_SPIN = -0.6;
const OUTER_SEGMENTS = 36;
const OUTER_BRIGHTNESS = 0.4;
/** Where along the beam the outer streams start pinching in to the knot. */
const KNOT_START = 0.78;
const CORE_TUNING: Partial<EssenceThreadTuning> = { flowSpeed: 0.25, waveMeters: 0.8, mistFloor: 0.3 };
const OUTER_TUNING: Partial<EssenceThreadTuning> = { flowSpeed: 0.35, waveMeters: 0.8, mistFloor: 0.35, brightness: OUTER_BRIGHTNESS };
/** Points laid per turn of a helix (the thread smooths between them). */
const HELIX_POINTS_PER_TURN = 12;

const PULSE_SPRITE: ParticleSprite = { family: 'circle', index: 2 };
const PULSE_PERIOD_SECONDS = 2.6;
const PULSE_LIFE_SECONDS = 1.6;
const PULSE_SIZE_FROM = 0.3;
const PULSE_SIZE_TO = 2;
/** The floor pulse's opacity at its strongest, as drawn in game (`LootBeam.setPulseAlpha`). */
export const LOOT_BEAM_PULSE_ALPHA = 0.55;
const KNOT_SPRITE: ParticleSprite = { family: 'flare', index: 1 };
const KNOT_SIZE_TILES = 0.32;
const KNOT_ALPHA = 0.8;
const GLYPH_SIZE_TILES = 0.55;
/** The glyph's center over the beam's top. */
const GLYPH_LIFT_TILES = 0.34;
const GLYPH_BOB_TILES = 0.03;

/** One thread wound about the beam's axis. */
export interface LootBeamHelix {
    readonly radius: number;
    readonly turnsPerTile: number;
    /** Its angle at the floor, in radians. */
    readonly phase: number;
    /** It pinches in to the axis near the top (the knot). */
    readonly knot: boolean;
}

/** The points a helix thread is laid through, from the floor up `height`. */
export function resolveLootBeamHelixPoints(floor: Vector3, height: number, helix: LootBeamHelix): Vector3[] {
    const turns = helix.turnsPerTile * height;
    const count = Math.max(6, Math.ceil(turns * HELIX_POINTS_PER_TURN) + 1);
    return Array.from({ length: count }, (_, index) => {
        const along = index / (count - 1);
        const radius = helix.radius * (helix.knot ? 1 - smoothstep(KNOT_START, 1, along) : 1);
        const angle = helix.phase + Math.PI * 2 * turns * along;
        return new Vector3(floor.x + Math.cos(angle) * radius, floor.y + height * along, floor.z + Math.sin(angle) * radius);
    });
}

/**
 * How far the floor ring has spread, `secondsSince` its pulse began: its
 * size across and its opacity. Null between pulses.
 */
export function resolveLootBeamPulse(secondsSince: number, peakAlpha = LOOT_BEAM_PULSE_ALPHA): { readonly size: number; readonly alpha: number } | null {
    const t = (((secondsSince % PULSE_PERIOD_SECONDS) + PULSE_PERIOD_SECONDS) % PULSE_PERIOD_SECONDS) / PULSE_LIFE_SECONDS;
    if (t >= 1) {
        return null;
    }
    const spread = 1 - (1 - t) * (1 - t);
    return {
        size: PULSE_SIZE_FROM + (PULSE_SIZE_TO - PULSE_SIZE_FROM) * spread,
        alpha: peakAlpha * Math.min(1, t / 0.08) * Math.pow(1 - t, 1.5),
    };
}

interface WoundThread {
    readonly thread: EssenceThread;
    /** Null for a straight core. */
    readonly helix: { readonly radius: number; readonly turnsPerTile: number; readonly phase: number; readonly spin: number; readonly knot: boolean } | null;
}

/**
 * A dropped item's column of light (#259), its tier read at a glance from
 * any distance by its shape alone, never by color: the core in the item's
 * essence types (one straight essence thread, or a tight twirl of them), the
 * Veil's white streams spinning wide around it
 * the other way, and from Unforgotten up a ring pulsing over the floor and
 * a knot of light at the top, where an Empyreal find wears the Veil's
 * glyph. Laid in world space every frame, so every thread faces the camera
 * truly. It rises and fades through `setGrowth`.
 */
export class LootBeam {
    private readonly threads: WoundThread[] = [];
    private readonly look: LootBeamLook;
    private readonly floor = Vector3.Zero();
    private heightShare = 1;
    private intensity = 1;
    private timeSeconds = 0;
    private pulseAlpha = LOOT_BEAM_PULSE_ALPHA;
    private readonly pulse: { readonly mesh: Mesh; readonly material: StandardMaterial } | null;
    private readonly knot: { readonly mesh: Mesh; readonly material: StandardMaterial } | null;
    private readonly glyph: { readonly mesh: Mesh; readonly material: ShaderMaterial } | null;

    constructor(name: string, scene: Scene, spec: LootBeamSpec) {
        this.look = LOOT_BEAM_LOOKS[spec.tier];
        const coreColors = spec.coreColorHexes.length > 0 ? spec.coreColorHexes : [VEIL_LIGHT.toHexString()];
        const coreTuning = { ...CORE_TUNING, brightness: this.look.brightness };
        if (coreColors.length === 1) {
            this.threads.push({
                thread: new EssenceThread(`${name}:core`, scene, { colorHex: coreColors[0]!, width: CORE_WIDTH_TILES * this.look.widthShare, bend: 0, strength: CORE_STRENGTH, segments: CORE_SEGMENTS, tuning: coreTuning }),
                helix: null,
            });
        } else {
            coreColors.forEach((colorHex, index) => {
                this.threads.push({
                    thread: new EssenceThread(`${name}:core-${index}`, scene, { colorHex, width: TWIN_CORE_WIDTH_TILES * this.look.widthShare, bend: 0, strength: CORE_STRENGTH, segments: TWIN_CORE_SEGMENTS, tuning: coreTuning, seed: index * 0.37 }),
                    helix: { radius: TWIN_CORE_RADIUS_TILES, turnsPerTile: TWIN_CORE_TURNS_PER_TILE, phase: (Math.PI * 2 * index) / coreColors.length, spin: TWIN_CORE_SPIN, knot: false },
                });
            });
        }
        const outerColor = VEIL_LIGHT.toHexString();
        for (let index = 0; index < this.look.outerStreams; index += 1) {
            this.threads.push({
                thread: new EssenceThread(`${name}:outer-${index}`, scene, { colorHex: outerColor, width: OUTER_WIDTH_TILES, bend: 0, strength: OUTER_STRENGTH, segments: OUTER_SEGMENTS, tuning: OUTER_TUNING, seed: 0.5 + index * 0.29 }),
                // Off the core's phase, and opposite each other when there are two.
                helix: { radius: OUTER_RADIUS_TILES, turnsPerTile: OUTER_TURNS_PER_TILE, phase: Math.PI / 2 + (Math.PI * 2 * index) / this.look.outerStreams, spin: OUTER_SPIN, knot: this.look.knot },
            });
        }

        this.pulse = this.look.pulse ? this.createSprite(`${name}:pulse`, scene, PULSE_SPRITE, VEIL_LIGHT, 'floor') : null;
        this.knot = this.look.knot ? this.createSprite(`${name}:knot`, scene, KNOT_SPRITE, VEIL_LIGHT, 'camera') : null;
        if (this.knot) {
            this.knot.mesh.scaling.setAll(KNOT_SIZE_TILES);
        }
        this.glyph = this.look.glyph ? this.createGlyph(`${name}:glyph`, scene, VEIL_LIGHT) : null;
    }

    /** Stands it on a floor position (scene space). */
    setFloor(floor: { readonly x: number; readonly y: number; readonly z: number }): void {
        this.floor.set(floor.x, floor.y, floor.z);
    }

    /** How much of its height it stands at (a share, past 1 for a spring) and how bright it is: for rising in and fading out. */
    setGrowth(heightShare: number, intensity: number): void {
        this.heightShare = heightShare;
        this.intensity = intensity;
    }

    /** The floor pulse's opacity at its strongest. */
    setPulseAlpha(alpha: number): void {
        this.pulseAlpha = alpha;
    }

    /** Winds the threads on and moves the pulse, the knot and the glyph. Call every frame. */
    update(deltaSeconds: number): void {
        this.timeSeconds += deltaSeconds;
        const height = Math.max(0.01, LOOT_BEAM_HEIGHT_TILES * this.heightShare);
        const top = this.floor.add(new Vector3(0, height, 0));
        for (const { thread, helix } of this.threads) {
            if (helix) {
                thread.setPath(resolveLootBeamHelixPoints(this.floor, height, { ...helix, phase: helix.phase + helix.spin * this.timeSeconds }));
            } else {
                thread.setEnds(this.floor, top);
            }
            thread.setIntensity(this.intensity);
            thread.update(deltaSeconds);
        }
        if (this.pulse) {
            const ring = resolveLootBeamPulse(this.timeSeconds, this.pulseAlpha);
            this.pulse.mesh.setEnabled(ring !== null);
            if (ring) {
                this.pulse.mesh.position.set(this.floor.x, this.floor.y + 0.015, this.floor.z);
                this.pulse.mesh.scaling.setAll(ring.size);
                this.pulse.material.alpha = ring.alpha * this.intensity;
            }
        }
        if (this.knot) {
            this.knot.mesh.position.copyFrom(top);
            this.knot.material.alpha = KNOT_ALPHA * this.intensity * (0.85 + 0.15 * Math.sin(this.timeSeconds * 3));
        }
        if (this.glyph) {
            this.glyph.mesh.position.set(top.x, top.y + GLYPH_LIFT_TILES + Math.sin(this.timeSeconds * 1.2) * GLYPH_BOB_TILES, top.z);
            this.glyph.material.setFloat('time', this.timeSeconds);
            this.glyph.material.setFloat('intensity', this.intensity);
        }
    }

    dispose(): void {
        this.threads.forEach(({ thread }) => thread.dispose());
        for (const part of [this.pulse, this.knot, this.glyph]) {
            // A sprite material leaves its shared sprite alone.
            part?.material.dispose();
            part?.mesh.dispose();
        }
    }

    private createSprite(name: string, scene: Scene, sprite: ParticleSprite, color: Color3, facing: 'floor' | 'camera'): { mesh: Mesh; material: StandardMaterial } {
        const mesh = MeshBuilder.CreatePlane(name, { size: 1 }, scene);
        if (facing === 'floor') {
            mesh.rotation.x = Math.PI / 2;
        } else {
            mesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
        }
        const material = createSpriteMaterial(scene, name, sprite, color, true);
        mesh.material = material;
        mesh.isPickable = false;
        return { mesh, material };
    }

    private createGlyph(name: string, scene: Scene, color: Color3): { mesh: Mesh; material: ShaderMaterial } {
        const material = createPrimedGlyphMaterial(scene, name);
        material.setColor3('glyphColor', color);
        material.setFloat('glyphStyle', PRIMED_GLYPH_STYLE_INDEX.veil);
        material.setFloat('socketCount', 0);
        for (let socket = 0; socket < PRIMED_GLYPH_MAX_SOCKETS; socket += 1) {
            material.setColor3(`socketColor${socket}`, color);
            material.setFloat(`socketFill${socket}`, 0);
        }
        material.setFloat('time', 0);
        material.setFloat('intensity', 1);
        const mesh = MeshBuilder.CreatePlane(name, { size: GLYPH_SIZE_TILES }, scene);
        mesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
        mesh.material = material;
        mesh.isPickable = false;
        return { mesh, material };
    }
}
